import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const AmCtx = createContext(null);
export const useAm = () => useContext(AmCtx);

export const money = (n) => (n == null ? "-" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
export const pretty = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

export const L = ({ children, req }) => <div className="text-[11px] text-slate-500 mb-1">{children}{req && <span className="text-red-500 ml-0.5">*</span>}</div>;

export const Tip = ({ children, tone = "info" }) => (
  <p className={`flex gap-2 text-xs rounded-lg p-3 ${tone === "warn" ? "bg-amber-500/10 text-amber-700" : "bg-slate-900 text-slate-400"}`}>
    <Info className="h-4 w-4 shrink-0 text-cyan-500" /><span>{children}</span>
  </p>
);

const TONES = { Draft: "bg-slate-500/15 text-slate-500", Creating: "bg-cyan-500/15 text-cyan-600", Failed: "bg-red-500/15 text-red-600", Paused: "bg-amber-500/15 text-amber-700",
  "In Review": "bg-blue-500/15 text-blue-600", Active: "bg-emerald-500/15 text-emerald-600", Rejected: "bg-red-500/15 text-red-600", Completed: "bg-slate-500/15 text-slate-600" };

export function StatusPill({ x, testid }) {
  const s = x.display_status;
  return (
    <span className="inline-flex items-center gap-1" data-testid={testid}>
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${TONES[s] || "bg-slate-500/15 text-slate-500"}`}>{s}</span>
      <span className={`text-[9px] uppercase tracking-wide ${x.on_meta ? "text-emerald-600" : "text-slate-500"}`}>{x.on_meta ? "on Meta" : "LaunchX360 only"}</span>
      {x.dirty && <span className="text-[9px] text-amber-600">unsent changes</span>}
    </span>
  );
}

export function Issues({ issues, id }) {
  const mine = (issues || []).filter((i) => i.id === id);
  if (!mine.length) return null;
  return (
    <ul className="space-y-1 text-xs" data-testid={`issues-${id}`}>
      {mine.map((i, n) => <li key={n} className={i.severity === "error" ? "text-red-600" : "text-amber-600"}>{i.severity === "error" ? "●" : "▲"} {i.message}</li>)}
    </ul>
  );
}

export function useEntity(level, entity) {
  const { setTree, flushRef } = useAm();
  const [d, setD] = useState(entity);
  const pending = useRef({});
  const timer = useRef(null);
  const [saving, setSaving] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    const body = pending.current; pending.current = {};
    if (!Object.keys(body).length) return;
    setSaving(true); setUnsaved(false);
    try {
      const r = await api.patch(`/ads-manager/${level}/${entity.id}`, body);
      (r.data.warnings || []).forEach((w) => toast.warning(w));
      setTree(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  }, [level, entity.id, setTree]);
  const set = useCallback((patch) => {
    setD((p) => ({ ...p, ...patch }));
    pending.current = { ...pending.current, ...patch };
    setUnsaved(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 800);
  }, [flush]);
  useEffect(() => { if (flushRef) flushRef.current = flush; }, [flush, flushRef]);
  useEffect(() => () => { clearTimeout(timer.current); if (Object.keys(pending.current).length) api.patch(`/ads-manager/${level}/${entity.id}`, pending.current).catch(() => {}); }, [level, entity.id]);
  return { d, set, setD, flush, saving: saving ? "Saving…" : unsaved ? "Unsaved changes" : "Saved" };
}

export function Counter({ v, soft, hard }) {
  const n = (v || "").length;
  return <span className={`text-[10px] ${n > hard ? "text-red-600" : n > soft ? "text-amber-600" : "text-slate-500"}`}>{n}/{soft} recommended · max {hard}</span>;
}
